import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';

import { Button } from '@llura/ui';

import { sendVerificationEmail, signOut, useSession } from '../../lib/auth-client';
import { useStrings } from '../../hooks/useStrings';

interface LocationState {
  email?: string;
}

export function VerifyEmailPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { strings } = useStrings();
  const t = strings.auth.verifyEmail;

  const session = useSession();
  const email = (location.state as LocationState | null)?.email ?? session.data?.user.email ?? '';

  const [resendState, setResendState] = useState<'idle' | 'sending' | 'sent'>('idle');

  // Poll the session every 5s — Better Auth updates `emailVerified` once the
  // user clicks the link in another tab. Cheap, no socket needed.
  useEffect(() => {
    if (!session.data) return;
    if (session.data.user.emailVerified) {
      navigate('/welcome', { replace: true });
      return;
    }
    const id = window.setInterval(() => {
      session.refetch?.();
    }, 5_000);
    return () => window.clearInterval(id);
  }, [session, navigate]);

  const resend = async () => {
    if (!email) return;
    setResendState('sending');
    try {
      await sendVerificationEmail({ email });
      setResendState('sent');
    } catch {
      setResendState('idle');
    }
  };

  const useDifferentEmail = async () => {
    await signOut();
    navigate('/signup', { replace: true });
  };

  return (
    <div className="relative min-h-[100svh] overflow-hidden bg-background">
      <div
        aria-hidden
        className="pointer-events-none absolute -top-32 right-[-15%] h-[420px] w-[420px] rounded-full bg-primary/10 blur-3xl"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute bottom-[-10%] left-[-10%] h-[380px] w-[380px] rounded-full bg-secondary/10 blur-3xl"
      />

      <header className="sticky top-0 z-10 flex items-center gap-2 bg-background/80 px-4 py-3 backdrop-blur">
        <span className="font-serif text-lg">{strings.brand.name}</span>
      </header>

      <main className="relative mx-auto w-full max-w-md px-5 pb-16 pt-12">
        <h1 className="font-serif text-3xl">{t.title}</h1>
        <p className="mt-3 text-sm text-muted-foreground">
          {t.body.replace('{email}', email)}
        </p>

        <div className="mt-8 space-y-3">
          <Button onClick={resend} variant="secondary" className="w-full" disabled={resendState === 'sending' || !email}>
            {resendState === 'sending'
              ? t.resendSending
              : resendState === 'sent'
                ? t.resendSent
                : t.resendCta}
          </Button>
          <Button onClick={useDifferentEmail} variant="ghost" className="w-full">
            {t.signOutCta}
          </Button>
        </div>

        <p className="mt-6 text-center text-sm text-muted-foreground">
          {t.signInPrompt}{' '}
          <Link to="/login" className="text-primary underline-offset-4 hover:underline">
            {t.signInCta}
          </Link>
        </p>
      </main>
    </div>
  );
}
